import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { OwnerService } from '../services/ownerService';
import { transformOwnerFormData, validateOwnerForm } from '../utils/ownerUtils';

/**
 * Custom hook for managing owner operations
 * Encapsulates owner-related state and business logic
 */
export function useOwners() {
    const { authorizedFetch } = useAuth();
    
    // State
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    /**
     * Load all owners from API
     */
    const loadOwners = async () => {
        if (!authorizedFetch) return;
        
        setLoading(true);
        setError('');
        
        try {
            const ownersData = await OwnerService.getAllOwners(authorizedFetch);
            setOwners(ownersData);
        } catch (err) {
            console.error('Failed to load owners:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Create a new owner
     * @param {Object} formData - Owner form data
     */
    const createOwner = async (formData) => {
        setError('');
        setValidationErrors({});
        
        // Validate form data
        const validation = validateOwnerForm(formData);
        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            throw new Error('Validation failed');
        }
        
        try {
            const ownerData = transformOwnerFormData(formData);
            const createdOwner = await OwnerService.createOwner(ownerData, authorizedFetch);
            setOwners(prev => [createdOwner, ...prev]);
            return createdOwner;
        } catch (err) {
            console.error('Failed to create owner:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Update an existing owner
     * @param {string} ownerId - ID of owner to update
     * @param {Object} formData - Updated owner form data
     */
    const updateOwner = async (ownerId, formData) => {
        setError('');
        setValidationErrors({});
        
        // Validate form data
        const validation = validateOwnerForm(formData);
        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            throw new Error('Validation failed');
        }
        
        try {
            const ownerData = transformOwnerFormData(formData);
            const updatedOwner = await OwnerService.updateOwner(ownerId, ownerData, authorizedFetch);
            setOwners(prev => prev.map(owner => 
                owner._id === ownerId ? updatedOwner : owner
            ));
            return updatedOwner;
        } catch (err) {
            console.error('Failed to update owner:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Delete an owner
     * @param {string} ownerId - ID of owner to delete
     */
    const deleteOwner = async (ownerId) => {
        setError('');
        
        try {
            await OwnerService.deleteOwner(ownerId, authorizedFetch);
            setOwners(prev => prev.filter(owner => owner._id !== ownerId));
        } catch (err) {
            console.error('Failed to delete owner:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Clear any existing errors
     */
    const clearError = () => {
        setError('');
        setValidationErrors({});
    };

    /**
     * Initialize data loading when authorizedFetch is available
     */
    useEffect(() => {
        if (authorizedFetch) {
            loadOwners();
        }
    }, [authorizedFetch]);

    return {
        owners,
        loading,
        error,
        validationErrors,
        loadOwners,
        createOwner,
        updateOwner,
        deleteOwner,
        clearError
    };
}
